import { useState } from 'react'
import { Download, FolderOpen, Loader2, Redo2, RotateCcw, Undo2 } from 'lucide-react'

import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { viewerApi } from '@viewer/lib/viewerApi'
import { aplicarEdicoes, edicoesDoEstado, totalDeEdicoes, type ResultadoDeGravar } from '@modules/ifc-edit/lib/exportarEdicoes'
import { bytesDoModelo, depoisDePintar } from './modeloEmTexto'
import RelatorioDeOperacoes from './RelatorioDeOperacoes'
import { AVISO, BTN, BTN_PRIMARIO, NOTA, OK } from './estilos'
// Com o painel Dados numa janela destacada, a pergunta aparece nela.
import { confirmar } from '@core/layout/janela-destacada/abrirJanela'

function quando(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export default function SeparadorAlteracoes({ modelId, ficheiro }: { modelId: string; ficheiro: string }) {
  const lotes = useAppStore((s) => s.ifcEditLotes)
  const anulados = useAppStore((s) => s.ifcEditLotesAnulados)
  const anular = useAppStore((s) => s.anularLote)
  const refazer = useAppStore((s) => s.refazerLote)
  const reset = useAppStore((s) => s.resetIfcEdit)
  const atributos = useAppStore((s) => s.ifcEditAttributeEdits)
  const classes = useAppStore((s) => s.ifcEditClassChanges)
  const psets = useAppStore((s) => s.ifcEditPsetEdits)
  const esquema = useAppStore((s) => s.models.find((m) => m.id === modelId)?.schemaVersion ?? null)
  const [aGravar, setAGravar] = useState(false)
  const [resultado, setResultado] = useState<ResultadoDeGravar | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const edicoes = edicoesDoEstado(
    { ifcEditAttributeEdits: atributos, ifcEditClassChanges: classes, ifcEditPsetEdits: psets, ifcEditLotes: lotes },
    esquema,
  )
  const noPainel = edicoes.atributos.length + edicoes.classes.length + edicoes.psets.length
  const total = totalDeEdicoes(edicoes)
  const base = ficheiro.replace(/\.ifc$/i, '')

  const gravar = async (): Promise<ResultadoDeGravar | null> => {
    const bytes = bytesDoModelo(modelId)
    if (!bytes) {
      setErro('Este modelo não tem o texto do IFC em memória. Abre o .ifc.')
      return null
    }
    setAGravar(true)
    setErro(null)
    await depoisDePintar()
    try {
      const r = aplicarEdicoes(bytes, edicoes)
      setResultado(r)
      return r
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
      return null
    } finally {
      setAGravar(false)
    }
  }

  const descarregar = async () => {
    const r = await gravar()
    if (!r) return
    downloadBlob(r.bytes.slice().buffer, `${base}_editado.ifc`)
    useAppStore.getState().pushToast(`${base}_editado.ifc descarregado. O original fica como estava.`, { type: 'success' })
  }

  const abrirNoViewer = async () => {
    if (
      !confirmar(
        'Substituir o modelo aberto pelo IFC com as alterações? As alterações pendentes ficam aplicadas no modelo novo e a lista esvazia. O ficheiro original no disco não muda.',
      )
    )
      return
    const r = await gravar()
    if (!r) return
    const f = new File([r.bytes.slice().buffer], `${base}_editado.ifc`)
    const aberto = await viewerApi.loadIfcFile(f, { replace: true })
    // Se o IFC editado não abriu, o modelo anterior fica e as alterações
    // pendentes também: esvaziar a lista perdia-as sem nada no ecrã.
    if (!aberto.ok) {
      useAppStore.getState().pushToast(`Não foi possível abrir o IFC editado: ${aberto.erro}`, { type: 'error' })
      return
    }
    reset()
    setResultado(null)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3 text-[11px]">
      {total === 0 && lotes.length === 0 ? (
        <p className={NOTA}>
          Sem alterações pendentes. Importa uma folha, muda o nome de um Pset, ou edita valores no painel de
          propriedades.
        </p>
      ) : (
        <>
          {noPainel > 0 && (
            <div className="text-[10px] text-[var(--color-text-muted)]">
              {noPainel} edição(ões) feitas no painel de propriedades (atributos, classes e valores).
            </div>
          )}
          <ol className="flex flex-col gap-1">
            {lotes.map((l, i) => (
              <li key={l.id} className="flex items-center gap-2 rounded border border-[var(--color-border)] px-2 py-1">
                <span className="font-mono text-[10px] text-[var(--color-text-dim)]">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate" title={l.descricao}>{l.descricao}</span>
                <span className="text-[10px] text-[var(--color-text-dim)]">{quando(l.quando)}</span>
              </li>
            ))}
          </ol>
        </>
      )}
      <div className="flex flex-wrap gap-1">
        <button type="button" className={BTN} onClick={anular} disabled={lotes.length === 0} title="Anular o último lote">
          <Undo2 size={12} /> Anular
        </button>
        <button type="button" className={BTN} onClick={refazer} disabled={anulados.length === 0} title="Refazer o último lote anulado">
          <Redo2 size={12} /> Refazer
        </button>
        <button
          type="button"
          className={BTN}
          disabled={total === 0}
          onClick={() => {
            if (confirmar('Limpar todas as alterações pendentes, incluindo as feitas no painel de propriedades?')) {
              reset()
              setResultado(null)
            }
          }}
        >
          <RotateCcw size={12} /> Limpar tudo
        </button>
      </div>
      <div className="mt-1 flex flex-wrap gap-1 border-t border-[var(--color-border)] pt-2">
        <button type="button" className={BTN_PRIMARIO} onClick={() => void descarregar()} disabled={aGravar || total === 0}>
          {aGravar ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Descarregar IFC com as alterações
        </button>
        <button type="button" className={BTN} onClick={() => void abrirNoViewer()} disabled={aGravar || total === 0}>
          <FolderOpen size={12} /> Abrir o resultado no viewer
        </button>
      </div>
      <p className={NOTA}>
        Abrir o resultado no viewer é a melhor verificação: o painel de propriedades e o inventário passam a ler o
        ficheiro novo.
      </p>
      {erro && <div className={AVISO}>{erro}</div>}
      {resultado && (
        <>
          {resultado.aplicadas + resultado.semCorrespondencia.length > 0 && (
            <div className={resultado.semCorrespondencia.length === 0 ? OK : AVISO}>
              Painel de propriedades: {resultado.aplicadas} aplicada(s)
              {resultado.semCorrespondencia.length > 0 && `, ${resultado.semCorrespondencia.length} sem correspondência no ficheiro`}.
            </div>
          )}
          {lotes.length > 0 && <RelatorioDeOperacoes r={resultado.relatorio} />}
        </>
      )}
    </div>
  )
}
